import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { SEO } from "@/components/SEO";
import { Download, Smartphone, Zap, Wifi } from "lucide-react";
import { Link } from "react-router-dom";
import { BackButton } from "@/components/BackButton";

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

const Install = () => {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [isInstallable, setIsInstallable] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);

  useEffect(() => {
    // Check if already installed
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setIsInstalled(true);
    }

    // Listen for the beforeinstallprompt event
    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
      setIsInstallable(true);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  const handleInstallClick = async () => {
    if (!deferredPrompt) return;

    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    
    if (outcome === 'accepted') {
      setIsInstalled(true);
    }
    
    setDeferredPrompt(null);
    setIsInstallable(false);
  };

  return (
    <>
      <SEO
        title="Install HiStage App - Get the Mobile Experience"
        description="Install HiStage on your device for the best experience. Enjoy fast loading, offline access, and app-like navigation."
        url="/install"
      />
      <div className="min-h-screen bg-gradient-to-b from-bg-50 to-white">
        <div className="container mx-auto px-4 sm:px-6 py-8 sm:py-12">
          <BackButton />
          
          <div className="max-w-2xl mx-auto mt-8 space-y-8">
            <div className="text-center space-y-4">
              <div className="inline-flex items-center justify-center w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-primary/10 mb-4">
                <Download className="w-8 h-8 sm:w-10 sm:h-10 text-primary" />
              </div>
              <h1 className="font-bold text-foreground">
                Install HiStage App
              </h1>
              <p className="text-muted-foreground max-w-xl mx-auto">
                Get the best experience with our installable app. Fast, reliable, and works offline.
              </p>
            </div>

            {isInstalled ? (
              <Card className="p-6 sm:p-8 text-center space-y-4">
                <div className="inline-flex items-center justify-center w-12 h-12 sm:w-16 sm:h-16 rounded-full bg-green-100 mb-2">
                  <Smartphone className="w-6 h-6 sm:w-8 sm:h-8 text-green-600" />
                </div>
                <h2 className="font-bold text-foreground">App Already Installed!</h2>
                <p className="text-muted-foreground">
                  You're running HiStage as an installed app. Enjoy the full experience!
                </p>
                <Link to="/">
                  <Button size="lg" className="w-full sm:w-auto">
                    Go to Home
                  </Button>
                </Link>
              </Card>
            ) : (
              <>
                <Card className="p-6 sm:p-8 space-y-6">
                  {isInstallable ? (
                    <div className="space-y-4">
                      <h2 className="font-bold text-foreground text-center">
                        Ready to Install
                      </h2>
                      <p className="text-muted-foreground text-center">
                        Click the button below to add HiStage to your home screen
                      </p>
                      <Button 
                        size="lg" 
                        className="w-full"
                        onClick={handleInstallClick}
                      >
                        <Download className="w-5 h-5 mr-2" />
                        Install App
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <h2 className="font-bold text-foreground">
                        How to Install
                      </h2>
                      <div className="space-y-4">
                        <div className="space-y-2">
                          <h3 className="font-semibold text-foreground">On iPhone/iPad:</h3>
                          <ol className="list-decimal list-inside space-y-1 text-sm text-muted-foreground">
                            <li>Tap the Share button (square with arrow)</li>
                            <li>Scroll down and tap "Add to Home Screen"</li>
                            <li>Tap "Add" to confirm</li>
                          </ol>
                        </div>
                        <div className="space-y-2">
                          <h3 className="font-semibold text-foreground">On Android:</h3>
                          <ol className="list-decimal list-inside space-y-1 text-sm text-muted-foreground">
                            <li>Tap the menu button (three dots)</li>
                            <li>Tap "Add to Home screen" or "Install app"</li>
                            <li>Tap "Add" or "Install" to confirm</li>
                          </ol>
                        </div>
                      </div>
                    </div>
                  )}
                </Card>

                <div className="grid sm:grid-cols-3 gap-4 sm:gap-6">
                  <Card className="p-4 sm:p-6 text-center space-y-3">
                    <div className="inline-flex items-center justify-center w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-primary/10">
                      <Zap className="w-5 h-5 sm:w-6 sm:h-6 text-primary" />
                    </div>
                    <h3 className="font-semibold text-foreground text-sm sm:text-base">Lightning Fast</h3>
                    <p className="text-xs sm:text-sm text-muted-foreground">
                      Instant loading and smooth performance
                    </p>
                  </Card>

                  <Card className="p-4 sm:p-6 text-center space-y-3">
                    <div className="inline-flex items-center justify-center w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-primary/10">
                      <Wifi className="w-5 h-5 sm:w-6 sm:h-6 text-primary" />
                    </div>
                    <h3 className="font-semibold text-foreground text-sm sm:text-base">Works Offline</h3>
                    <p className="text-xs sm:text-sm text-muted-foreground">
                      Access your content even without internet
                    </p>
                  </Card>

                  <Card className="p-4 sm:p-6 text-center space-y-3">
                    <div className="inline-flex items-center justify-center w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-primary/10">
                      <Smartphone className="w-5 h-5 sm:w-6 sm:h-6 text-primary" />
                    </div>
                    <h3 className="font-semibold text-foreground text-sm sm:text-base">App-Like Feel</h3>
                    <p className="text-xs sm:text-sm text-muted-foreground">
                      Full screen mode with no browser bars
                    </p>
                  </Card>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default Install;
